import htmlToPdfmake from 'html-to-pdfmake';
import { logger } from './logging-utils';

function sanitizeTablesForPdfMake(docDefinition: any): void {
  const tables: any[] = [];

  function recursivelyFindTables(node: any) {
    if (!node) {
      return;
    }

    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i += 1) {
        recursivelyFindTables(node[i]);
      }
    } else if (typeof node === 'object') {
      if (node.table?.body) {
        tables.push(node.table.body);
      }
      if (node.stack) {
        recursivelyFindTables(node.stack);
      }
      if (node.columns) {
        recursivelyFindTables(node.columns);
      }
      if (node.ul) {
        recursivelyFindTables(node.ul);
      }
      if (node.ol) {
        recursivelyFindTables(node.ol);
      }
      if (Array.isArray(node.text)) {
        recursivelyFindTables(node.text);
      }
    }
  }

  try {
    recursivelyFindTables(docDefinition);

    tables.forEach((body) => {
      const expectedLength = body[0]?.length || 0;

      body.forEach((row: any[], rowIndex: number) => {
        row.forEach((cell, colIndex) => {
          if (cell?.rowSpan && cell.rowSpan > 1) {
            const span = cell.rowSpan;
            delete cell.rowSpan;

            for (let i = 1; i < span; i += 1) {
              const targetRow = body[rowIndex + i];
              if (targetRow) {
                targetRow.splice(colIndex, 0, {
                  text: '',
                  style: cell.style,
                });
              }
            }
          }
        });

        if (row.length > expectedLength) {
          const startIndex = row.length - expectedLength;
          row.splice(0, startIndex);
        }
      });
    });
  } catch (error) {
    logger.log('warning', {
      message: 'sanitizeTablesForPdfMake failed — skipping table sanitization.',
      error,
    });
  }
}

function cleanHtml(htmlString: string) {
  // Create a temporary DOM element to parse the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Remove id attributes
  doc.querySelectorAll('[id]').forEach((element) => {
    element.removeAttribute('id');
  });

  // Process style attributes
  doc.querySelectorAll('[style]').forEach((element) => {
    const styleAttribute = element.getAttribute('style');
    if (styleAttribute) {
      const styles = styleAttribute.split(';');
      const filteredStyles = styles.filter((style) => {
        const [property, value] = style.trim().split(':');
        if (property === 'font-size') {
          return /^\s*\d+px\s*$/.test(value); // Keep only if it's in the form of '16px'
        }
        return true;
      });
      element.setAttribute('style', filteredStyles.join('; '));
    }
  });

  const cleanedHtml = doc.body.innerHTML;

  // remove whitespace between tags
  return cleanedHtml.replace(/>\s*\n\s*</g, '><');
}

export const htmlToPdfMakeWrapper = (html: string) => {
  const cleanedHtml = cleanHtml(html);

  const docDefinition = htmlToPdfmake(cleanedHtml, {
    defaultStyles: {
      h1: { fontSize: 20, bold: true, marginBottom: 5 },
      h2: { fontSize: 18, bold: true, marginBottom: 5 },
      h3: { fontSize: 14, bold: true, marginBottom: 5 },
      h4: { fontSize: 14, bold: true, marginBottom: 5 },
      h5: { fontSize: 12, bold: true, marginBottom: 5 },
      h6: { fontSize: 10, bold: true, marginBottom: 5 },
    },
    ignoreStyles: ['font-family'],
    removeExtraBlanks: true,
    tableAutoSize: true,
  });

  // 🩹 Sanitize broken tables before passing to pdfMake
  sanitizeTablesForPdfMake(docDefinition);
  return docDefinition;
};
